/**
 * Local storage service for tracking part interest submissions
 */

export interface InterestSubmission {
  partId: string;
  submittedAt: string;
  email: string;
}

interface StorageData {
  submissions: InterestSubmission[];
}

export interface PartInterestStorage {
  addSubmission(partId: string, email: string): void;
  getRecentSubmissions(partId: string, days: number): InterestSubmission[];
  hasRecentSubmission(partId: string, hours: number): boolean;
  cleanup(): void;
}

const STORAGE_KEY = 'power-up-part-interest';

class PartInterestStorageImpl implements PartInterestStorage {
  private getStorageData(): StorageData {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (!data) {
        return { submissions: [] };
      }
      return JSON.parse(data) as StorageData;
    } catch (error) {
      console.warn('Failed to parse part interest storage data:', error);
      return { submissions: [] };
    }
  }

  private setStorageData(data: StorageData): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Failed to save part interest storage data:', error);
    }
  }

  addSubmission(partId: string, email: string): void {
    const data = this.getStorageData();
    const submission: InterestSubmission = {
      partId,
      email,
      submittedAt: new Date().toISOString(),
    };
    
    data.submissions.push(submission);
    this.setStorageData(data);
  }

  getRecentSubmissions(partId: string, days: number): InterestSubmission[] {
    const data = this.getStorageData();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    
    return data.submissions.filter(submission => {
      if (submission.partId !== partId) {
        return false;
      }
      
      const submissionDate = new Date(submission.submittedAt);
      return submissionDate >= cutoffDate;
    });
  }

  hasRecentSubmission(partId: string, hours: number): boolean {
    const data = this.getStorageData();
    const cutoffTime = new Date();
    cutoffTime.setHours(cutoffTime.getHours() - hours);
    
    return data.submissions.some(submission => {
      if (submission.partId !== partId) {
        return false;
      }
      
      const submissionTime = new Date(submission.submittedAt);
      return submissionTime >= cutoffTime;
    });
  }

  cleanup(): void {
    const data = this.getStorageData();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - 7);
    
    data.submissions = data.submissions.filter(submission => {
      const submissionDate = new Date(submission.submittedAt);
      return submissionDate >= cutoffDate;
    });
    
    this.setStorageData(data);
  }
}

export const partInterestStorage: PartInterestStorage = new PartInterestStorageImpl();